import { logError } from "@/lib/log";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatINR, formatDate } from "@/lib/format";
import { CategorySelect } from "@/components/categories/CategorySelect";
import { toast } from "sonner";
import { Plus, Trash2, Check, CreditCard } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { openRazorpayCheckout } from "@/lib/razorpay";

function UnpaidBillItem({ bill: b, payingId, onTogglePaid, onPayNow, onDelete }) {
  return (
    <li className="flex items-center justify-between gap-4 p-4 flex-wrap">
      <div className="flex items-center gap-4">
        <button data-testid={`pay-bill-${b.id}`} onClick={() => onTogglePaid(b)} className="w-7 h-7 border border-[#1A1F16] flex items-center justify-center hover:bg-[#2E6F40] hover:border-[#2E6F40] hover:text-white"><Check className="w-3.5 h-3.5 opacity-0 hover:opacity-100" /></button>
        <div>
          <div className="font-sans text-base">{b.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{b.category} · {b.recurring} · Due {formatDate(b.due_date)}</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="num font-serif text-2xl">{formatINR(b.amount)}</div>
        <button
          data-testid={`pay-now-${b.id}`}
          onClick={() => onPayNow(b)}
          disabled={payingId === b.id}
          className="bg-[#1A1F16] text-[#F7F5F0] px-4 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-[#2C3628] disabled:opacity-50 flex items-center gap-2"
        >
          <CreditCard className="w-3.5 h-3.5" /> {payingId === b.id ? "Opening…" : "Pay now"}
        </button>
        <button data-testid={`delete-bill-${b.id}`} onClick={() => onDelete(b.id)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
}

function PaidBillItem({ bill: b, onTogglePaid }) {
  return (
    <li className="flex items-center justify-between p-4 opacity-70">
      <div className="flex items-center gap-4">
        <button onClick={() => onTogglePaid(b)} className="w-7 h-7 bg-[#2E6F40] text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></button>
        <div>
          <div className="font-sans text-base line-through">{b.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{b.category} · {formatDate(b.due_date)}</div>
          {b.razorpay_payment_id && (
            <div className="font-mono text-[10px] tracking-wider text-[#2E6F40] mt-0.5" data-testid={`payment-ref-${b.id}`}>
              Paid via Razorpay · {b.razorpay_payment_id} · {formatDate(b.paid_at)}
            </div>
          )}
        </div>
      </div>
      <div className="num font-serif text-2xl text-[#6C7565]">{formatINR(b.amount)}</div>
    </li>
  );
}

function BillFormModal({ form, setForm, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="bill-form">
        <h3 className="font-serif text-3xl mb-6">New bill</h3>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Name</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bill-name" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" placeholder="e.g. Electricity" />
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Amount (₹)</span>
          <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bill-amount" className="w-full bg-transparent border-b border-[#1A1F16] py-2 font-serif text-2xl num focus:outline-none" />
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category</span>
          <CategorySelect type="expense" value={form.category} onChange={(c) => setForm({ ...form, category: c })} testid="bill-category" />
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Due date</span>
          <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="bill-date" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
        </label>
        <label className="block mb-6">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Recurrence</span>
          <select value={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.value })} data-testid="bill-recurring" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
            <option value="once">Once</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="bill-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">Save</button>
        </div>
      </form>
    </div>
  );
}

/** Bill list state + actions (mark paid, delete, Razorpay pay-now flow). */
function useBills() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => { const { data } = await api.get("/bills"); setItems(data); };
  useEffect(() => { load(); }, []);

  const togglePaid = async (b) => {
    const { data } = await api.put(`/bills/${b.id}`, { ...b, paid: !b.paid });
    if (data?.next_bill) {
      toast.success(`Bill paid — next "${data.next_bill.name}" scheduled for ${formatDate(data.next_bill.due_date)}`);
    }
    load();
  };

  const remove = (id) => {
    const b = items.find((x) => x.id === id);
    setConfirm({
      title: "Delete bill?",
      detail: `"${b?.name || "This bill"}" (${formatINR(b?.amount || 0)}) will be permanently deleted.`,
      run: async () => { await api.delete(`/bills/${id}`); toast.success("Bill deleted"); load(); },
    });
  };

  const payNow = async (b) => {
    setPayingId(b.id);
    try {
      const { data: order } = await api.post(`/payments/bills/${b.id}/order`);
      openRazorpayCheckout({
        order,
        description: `Bill: ${order.bill_name}`,
        user,
        onSettled: () => setPayingId(null),
        onSuccess: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — bill marked paid & expense recorded");
            load();
          } catch (e) {
            logError("Payment verification failed", e);
            toast.error("Payment verification failed — contact support with your payment ID");
          } finally {
            setPayingId(null);
          }
        },
      });
    } catch (e) {
      logError("Order creation failed", e);
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPayingId(null);
    }
  };

  return { items, load, payingId, confirm, setConfirm, togglePaid, remove, payNow };
}

export default function Bills() {
  const { items, load, payingId, confirm, setConfirm, togglePaid, remove, payNow } = useBills();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", amount: "", category: "Utilities", recurring: "monthly",
    due_date: new Date().toISOString().slice(0, 10), paid: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bills", { ...form, amount: parseFloat(form.amount), due_date: new Date(form.due_date).toISOString() });
      toast.success("Bill saved");
      setOpen(false);
      setForm({ ...form, name: "", amount: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const upcoming = items.filter((b) => !b.paid);
  const paid = items.filter((b) => b.paid);
  const totalDue = upcoming.reduce((s, b) => s + b.amount, 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section V</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Bills.</h2>
        </div>
        <button data-testid="add-bill-btn" onClick={() => setOpen(true)} className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add bill
        </button>
      </div>

      <div className="border border-[#D8D4C7] bg-white p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">Outstanding</div>
        <div className="font-serif text-4xl num mt-2" data-testid="bills-outstanding">{formatINR(totalDue)}</div>
      </div>

      <section>
        <h3 className="font-serif text-2xl mb-4">Upcoming</h3>
        {upcoming.length === 0 ? (
          <div className="text-sm text-[#6C7565] italic">All clear. Nothing due.</div>
        ) : (
          <ul className="divide-y divide-[#D8D4C7] border border-[#D8D4C7] bg-white" data-testid="upcoming-bills">
            {upcoming.map((b) => (
              <UnpaidBillItem key={b.id} bill={b} payingId={payingId} onTogglePaid={togglePaid} onPayNow={payNow} onDelete={remove} />
            ))}
          </ul>
        )}
      </section>

      {paid.length > 0 && (
        <section>
          <h3 className="font-serif text-2xl mb-4 text-[#6C7565]">Paid</h3>
          <ul className="divide-y divide-[#EAE7DD] border border-[#D8D4C7] bg-white" data-testid="paid-bills">
            {paid.map((b) => (
              <PaidBillItem key={b.id} bill={b} onTogglePaid={togglePaid} />
            ))}
          </ul>
        </section>
      )}

      {open && <BillFormModal form={form} setForm={setForm} onSubmit={submit} onClose={() => setOpen(false)} />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
